import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Sparkles, Loader, TrendingUp, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart, Line, Area } from 'recharts';

const Analytics = () => {
  const [incomeByCategory, setIncomeByCategory] = useState([]);
  const [expenseByCategory, setExpenseByCategory] = useState([]);
  const [aiInsights, setAiInsights] = useState('');
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [forecastModel, setForecastModel] = useState('default');
  const [forecastModelOptions, setForecastModelOptions] = useState([
    { value: 'default', label: 'Default (server preset)', provider: null, name: null },
  ]);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [incomeRes, expenseRes] = await Promise.all([
        axios.get(`${API}/analytics/by-category`, { params: { type: 'income' } }),
        axios.get(`${API}/analytics/by-category`, { params: { type: 'expense' } }),
      ]);

      setIncomeByCategory(incomeRes.data);
      setExpenseByCategory(expenseRes.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
    // Load the server-side whitelist for forecast models
    axios
      .get(`${API}/ai/forecast/models`)
      .then((r) => {
        const labelize = (p, n) => {
          if (p === 'openai') return `OpenAI · ${n.toUpperCase()}`;
          if (p === 'anthropic') return `Anthropic · ${n.replace(/-/g, ' ')}`;
          if (p === 'gemini') return `Gemini · ${n.replace(/-/g, ' ')}`;
          return `${p} · ${n}`;
        };
        const opts = [{ value: 'default', label: 'Default (server preset)', provider: null, name: null }];
        (r.data?.models || []).forEach((m) => {
          opts.push({ value: m.value, label: labelize(m.provider, m.name), provider: m.provider, name: m.name });
        });
        setForecastModelOptions(opts);
      })
      .catch(() => {
        /* fall back to single default option */
      });
  }, [fetchAnalytics]);

  const generateInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await axios.post(`${API}/ai/insights`, {});
      setAiInsights(response.data.insights);
      toast.success('AI insights generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate insights');
    } finally {
      setLoadingInsights(false);
    }
  };

  const generateForecast = async () => {
    setLoadingForecast(true);
    try {
      const opt = forecastModelOptions.find((m) => m.value === forecastModel) || forecastModelOptions[0];
      const params = {};
      if (opt.provider && opt.name) {
        params.model_provider = opt.provider;
        params.model_name = opt.name;
      }
      const response = await axios.post(`${API}/ai/forecast`, {}, { params });
      setForecast(response.data);
      toast.success('Forecast generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate forecast');
    } finally {
      setLoadingForecast(false);
    }
  };

  const exportForecastExcel = async () => {
    try {
      const res = await axios.get(`${API}/export/forecast`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `forecast-${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel forecast downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Excel export failed');
    }
  };

  const COLORS = ['#0F172A', '#10B981', '#4F46E5', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  return (
    <div className="p-8 md:p-12" data-testid="analytics-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8">
          <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="analytics-title">Analytics</h1>
          <p className="text-slate-600 mt-1 font-ibm">Deep dive into financial data and AI-powered insights</p>
        </div>

        {loading ? (
          <div className="text-slate-900 font-ibm" data-testid="analytics-loading">Loading analytics...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white border border-slate-200 p-6 rounded-sm"
                data-testid="chart-income-breakdown"
              >
                <h2 className="text-xl font-chivo font-bold text-slate-900 mb-4">Income by Category</h2>
                {incomeByCategory.length === 0 ? (
                  <p className="text-slate-600 font-ibm text-sm">No income data available</p>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={incomeByCategory}
                        dataKey="amount"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {incomeByCategory.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '2px', color: '#F8FAFC' }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white border border-slate-200 p-6 rounded-sm"
                data-testid="chart-expense-breakdown"
              >
                <h2 className="text-xl font-chivo font-bold text-slate-900 mb-4">Expenses by Category</h2>
                {expenseByCategory.length === 0 ? (
                  <p className="text-slate-600 font-ibm text-sm">No expense data available</p>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={expenseByCategory}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="category" stroke="#64748B" style={{ fontSize: '12px' }} />
                      <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '2px', color: '#F8FAFC' }}
                      />
                      <Bar dataKey="amount" fill="#EF4444" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white border border-slate-200 p-6 rounded-sm"
              data-testid="ai-insights-section"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-chivo font-bold text-slate-900">AI-Powered Insights</h2>
                  <p className="text-slate-600 text-sm font-ibm mt-1">Get intelligent recommendations based on your financial data</p>
                </div>
                <Button
                  onClick={generateInsights}
                  disabled={loadingInsights}
                  data-testid="generate-insights-button"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-sm hover:scale-[0.98] transition-transform"
                >
                  {loadingInsights ? (
                    <>
                      <Loader size={18} className="mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} className="mr-2" />
                      Generate Insights
                    </>
                  )}
                </Button>
              </div>

              {aiInsights ? (
                <div className="mt-6 p-6 bg-slate-50 border border-slate-200 rounded-sm" data-testid="ai-insights-content">
                  <div className="font-ibm text-slate-900 whitespace-pre-wrap leading-relaxed">{aiInsights}</div>
                </div>
              ) : (
                <div className="mt-6 p-12 text-center border border-dashed border-slate-300 rounded-sm" data-testid="ai-insights-placeholder">
                  <Sparkles size={48} className="mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-600 font-ibm">Click &quot;Generate Insights&quot; to get AI-powered financial analysis</p>
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-white border border-slate-200 p-6 rounded-sm mt-6"
              data-testid="forecast-section"
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h2 className="text-xl font-chivo font-bold text-slate-900">AI Forecast</h2>
                  <p className="text-slate-600 text-sm font-ibm mt-1">
                    Project the next 3 months of income, expenses, and profit based on your history
                  </p>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <Select value={forecastModel} onValueChange={setForecastModel}>
                    <SelectTrigger
                      data-testid="forecast-model-select"
                      className="bg-slate-50 border-slate-200 rounded-sm w-56 h-10 text-xs"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {forecastModelOptions.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={generateForecast}
                    disabled={loadingForecast}
                    data-testid="generate-forecast-button"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm hover:scale-[0.98] transition-transform"
                  >
                    {loadingForecast ? (
                      <>
                        <Loader size={18} className="mr-2 animate-spin" />
                        Forecasting...
                      </>
                    ) : (
                      <>
                        <TrendingUp size={18} className="mr-2" />
                        Generate Forecast
                      </>
                    )}
                  </Button>
                  {forecast?.parsed?.forecast && (
                    <Button
                      onClick={exportForecastExcel}
                      data-testid="export-forecast-xlsx-button"
                      className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
                    >
                      <Download size={18} className="mr-2" /> Excel
                    </Button>
                  )}
                </div>
              </div>

              {forecast?.parsed?.forecast ? (
                <div className="mt-6 space-y-4" data-testid="forecast-content">
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart
                      data={[
                        ...(forecast.history || []).map((h) => ({
                          month: h.month,
                          income: h.income,
                          expenses: h.expenses,
                          profit: h.income - h.expenses,
                          kind: 'history',
                        })),
                        ...forecast.parsed.forecast.map((f) => ({
                          month: f.month,
                          income: f.income,
                          expenses: f.expenses,
                          profit: f.profit,
                          kind: 'forecast',
                        })),
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="month" stroke="#64748B" style={{ fontSize: '12px' }} />
                      <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                      <Tooltip contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '2px', color: '#F8FAFC' }} />
                      <Legend />
                      <Bar dataKey="income" fill="#10B981" name="Income" />
                      <Bar dataKey="expenses" fill="#EF4444" name="Expenses" />
                      <Line type="monotone" dataKey="profit" stroke="#0F172A" strokeWidth={2} name="Profit" />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {forecast.parsed.forecast.map((f) => (
                      <div
                        key={f.month}
                        className="border border-slate-200 rounded-sm p-4"
                        data-testid={`forecast-month-${f.month}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600">
                            {f.month}
                          </span>
                          <span
                            className={`text-xs font-ibm font-semibold uppercase tracking-widest px-2 py-0.5 rounded-sm ${
                              f.confidence === 'high'
                                ? 'bg-emerald-50 text-emerald-700'
                                : f.confidence === 'medium'
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {f.confidence} conf.
                          </span>
                        </div>
                        <div className="mt-2 space-y-1">
                          <div className="flex justify-between text-sm font-ibm">
                            <span className="text-emerald-600">+ Income</span>
                            <span className="tabular-nums text-slate-900">${f.income?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm font-ibm">
                            <span className="text-red-600">- Expenses</span>
                            <span className="tabular-nums text-slate-900">${f.expenses?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-base font-ibm font-semibold pt-1 border-t border-slate-100">
                            <span>Profit</span>
                            <span
                              className={`tabular-nums ${
                                (f.profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                              }`}
                            >
                              ${f.profit?.toLocaleString()}
                            </span>
                          </div>
                          {(f.profit_low != null && f.profit_high != null) && (
                            <div
                              className="text-xs font-ibm text-slate-500 tabular-nums pt-1"
                              data-testid={`forecast-confidence-range-${f.month}`}
                            >
                              Range: ${f.profit_low?.toLocaleString()} – ${f.profit_high?.toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {forecast.parsed.narrative && (
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm" data-testid="forecast-narrative">
                      <p className="text-sm font-ibm text-slate-700 leading-relaxed">{forecast.parsed.narrative}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-6 p-12 text-center border border-dashed border-slate-300 rounded-sm" data-testid="forecast-placeholder">
                  <TrendingUp size={48} className="mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-600 font-ibm">Click &quot;Generate Forecast&quot; to project your next 3 months</p>
                </div>
              )}
            </motion.div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default Analytics;